import { Modal } from '@citizen-walk/shared-ui';
import {
  isPhotoPublished,
  type AiAnalysisBousaiResult,
  type AiAnalysisWalkerResult,
  type AiVerificationAssignmentSet,
  type PostPhotoMeta,
  type PostPhotoPrivacy,
} from '@citizen-walk/shared-types';
import { doc, getDoc, Timestamp, type DocumentData } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { db } from '../../lib/firebase';
import { useMyIdentity } from '../auth';
import type { PostMinimal } from '../posts';
import { useAssignmentSets } from './useAssignmentSets';
import { useDistrictVerificationConfig } from './useDistrictVerificationConfig';
import { useFreeVerificationCandidates } from './useFreeVerificationCandidates';
import { useMyVerification } from './useMyVerification';
import { useMyVerificationCount } from './useMyVerificationCount';
import { useVerificationTemplate } from './useTemplate';
import { VerificationForm, type VerificationFormPost } from './VerificationForm';

/**
 * AI 解析の検証 — published AssignmentSet 一覧 + 各 set の割当投稿一覧 (Sprint V)
 * + 自由検証 (free distribution) セクション (Sprint Y で追加)。
 *
 * 動作:
 *   - useAssignmentSets で status='published' な set 一覧を subscribe
 *   - 各 set ごとに distribution + perUserAssignments / candidatePostIds から uid 用 postIds を算出
 *   - postId 群を click で expand し、各 post を「回答する / ✓回答済」 button で開く
 *   - VerificationForm を nested Modal で表示
 *
 * Sprint Y (自由検証):
 *   - useDistrictVerificationConfig で aiVerification config を subscribe
 *   - `allowFreeVerification === true && activeTemplateId !== null` のとき page 上部に
 *     「📋 自由検証」セクションを追加
 *   - useFreeVerificationCandidates (AI 解析済 post 一覧) を listing
 *   - 配分 set 一覧の distribution='free' は List page では非表示 (free 経路は config で表示するため)
 *
 * scope 外 (別 sprint):
 *   - 集計 (consensus / Summary) 表示
 *   - random_per_user の client-side 配分 (admin 側で perUserAssignments を pre-compute する前提)
 */

export interface VerificationListPageProps {
  districtId: string;
  /** 研究利用説明 banner / 撤回 confirm 文言で使う地区名 (Phase G、old2 逐語)。 */
  districtName?: string;
  uid: string;
  currentSessionId: string | null;
  open: boolean;
  onClose: () => void;
}

interface PostSummary {
  id: string;
  title: string;
  authorNickname: string;
  /** 投稿者 UID (検証回答に写す / 「自分の投稿か」判定用)。旧 doc で欠けていれば null。 */
  authorUid: string | null;
}

interface PostFull extends PostSummary {
  comment: string | null;
  aiAnalysisAnalyzedAt: string | null;
  aiAnalysisSettingsId: string | null;
  aiAnalysisEcw: PostMinimal['aiAnalysisEcw'];
  aiAnalysisBousai: AiAnalysisBousaiResult | null;
  aiAnalysisWalker: AiAnalysisWalkerResult | null;
  /** MD-4 モードドリブン化: fields[] 駆動の汎用結果 (新規地区の唯一の解析結果)。 */
  aiAnalysisGeneric: Record<string, unknown> | null;
  // Phase G: 検証フォームの投稿表示 (写真 + 投稿位置の地図) 用 field。
  photoPath: string | null;
  photoThumbPath: string | null;
  photoUrl: string | null;
  /** 写真の幾何メタ (docs/27)。 */
  photo?: PostPhotoMeta | null;
  photoThumbUrl: string | null;
  location: { latitude: number; longitude: number } | null;
}

function tsToIso(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  return null;
}

/** Firestore GeoPoint / {latitude,longitude} を {latitude,longitude} に narrow (写真位置地図用)。 */
function asLocation(value: unknown): { latitude: number; longitude: number } | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as { latitude?: unknown; longitude?: unknown };
  if (typeof v.latitude === 'number' && typeof v.longitude === 'number') {
    return { latitude: v.latitude, longitude: v.longitude };
  }
  return null;
}

function mapPostSummary(snap: {
  id: string;
  data: () => DocumentData | undefined;
}): PostFull | null {
  const data = snap.data();
  if (!data) return null;
  const ai = (data.aiAnalysis as Record<string, unknown> | undefined) ?? undefined;
  // docs/28 §4-3・§4-9: 顔・ナンバーのぼかしが終わっていない・失敗・公開しない写真は出さない
  // (検証で割り当てられるのはほかの参加者の投稿。この画面は usePosts を通らないのでここで落とす)。
  const photoPublished = isPhotoPublished({
    photoPrivacy: data.photoPrivacy as PostPhotoPrivacy | null | undefined,
  });
  const photoField = (key: 'photoPath' | 'photoThumbPath' | 'photoUrl' | 'photoThumbUrl') =>
    photoPublished && typeof data[key] === 'string' ? (data[key] as string) : null;
  return {
    id: snap.id,
    title: typeof data.title === 'string' ? data.title : '(無題)',
    authorNickname: typeof data.authorNickname === 'string' ? data.authorNickname : '匿名',
    authorUid: typeof data.authorUid === 'string' && data.authorUid !== '' ? data.authorUid : null,
    comment: typeof data.comment === 'string' ? data.comment : null,
    aiAnalysisAnalyzedAt: tsToIso(ai?.analyzedAt) ?? null,
    aiAnalysisSettingsId: ai && typeof ai.settingsId === 'string' ? ai.settingsId : null,
    aiAnalysisEcw: (ai?.ecw as PostMinimal['aiAnalysisEcw']) ?? null,
    aiAnalysisBousai: (ai?.bousai as AiAnalysisBousaiResult | null) ?? null,
    aiAnalysisWalker: (ai?.walker as AiAnalysisWalkerResult | null) ?? null,
    // MD-4: generic は非空 object のときのみ採用 (usePosts.mapPostDoc と同方針)。
    aiAnalysisGeneric:
      ai?.generic &&
      typeof ai.generic === 'object' &&
      !Array.isArray(ai.generic) &&
      Object.keys(ai.generic as Record<string, unknown>).length > 0
        ? (ai.generic as Record<string, unknown>)
        : null,
    photoPath: photoField('photoPath'),
    photoThumbPath: photoField('photoThumbPath'),
    photoUrl: photoField('photoUrl'),
    photoThumbUrl: photoField('photoThumbUrl'),
    photo: (data.photo as PostPhotoMeta | null | undefined) ?? null,
    location: asLocation(data.location),
  };
}

/**
 * perUserAssignments から「自分 (現 UID + 端末引き継ぎ元 UID 群) 宛て」の投稿 ID を union する。
 * 順序は uids の順 → 各 UID 内の元順を保ち、重複は除去する。
 */
function unionPerUserAssignments(
  set: AiVerificationAssignmentSet,
  uid: string,
  selfUids?: string[],
): string[] {
  const uids = selfUids && selfUids.length > 0 ? selfUids : [uid];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const u of uids) {
    for (const pid of set.perUserAssignments?.[u] ?? []) {
      if (!seen.has(pid)) {
        seen.add(pid);
        out.push(pid);
      }
    }
  }
  return out;
}

/**
 * distribution + uid → 割当 postIds を解決 (pure、test 用に export)。
 *
 * R-70 P3b: `selfUids` (現 UID + 端末引き継ぎ元 UID 群) を渡すと、各 UID の割当を union する
 * (引き継ぎ前の旧端末 UID 宛てに配分された投稿も自分の割当として見えるようにする)。
 *
 * all_same × sameSessionOnly (2026-09-04): 「全員に全投稿」でも、生成時に「参加セッションの投稿だけを
 * 配る」(generationConstraints.sameSessionOnly) が指定されたセットは perUserAssignments に参加者ごとの
 * 絞り込み結果を持つので、それがあればそちらを正とする。旧セット (制約無し / perUserAssignments 無し)
 * は従来どおり候補全件 (挙動不変)。
 */
export function resolveAssignedPostIds(
  set: AiVerificationAssignmentSet,
  uid: string,
  selfUids?: string[],
): string[] {
  switch (set.distribution) {
    case 'all_same':
      if (set.generationConstraints?.sameSessionOnly === true && set.perUserAssignments) {
        return unionPerUserAssignments(set, uid, selfUids);
      }
      return set.candidatePostIds;
    case 'random_per_user':
    case 'manual':
      return unionPerUserAssignments(set, uid, selfUids);
    case 'free':
      // free モードは scope 外、UI 側で別 message
      return [];
    default:
      return [];
  }
}

/** 配分方式の表示名 (UI に内部キーを出さない)。 */
function distributionLabel(d: AiVerificationAssignmentSet['distribution']): string {
  switch (d) {
    case 'all_same':
      return '全員に全投稿';
    case 'random_per_user':
      return '参加者ごとにランダム';
    case 'manual':
      return '手動配分';
    case 'free':
      return '自由検証';
    default:
      return '—';
  }
}

interface AssignmentSetRowProps {
  set: AiVerificationAssignmentSet;
  uid: string;
  /** R-70 P3b: 現 UID + 端末引き継ぎ元 UID 群 (割当解決 / 回答済み判定の横断用)。 */
  selfUids: string[];
  districtId: string;
  expanded: boolean;
  onToggle: () => void;
  onOpenForm: (postId: string) => void;
}

function AssignmentSetRow({
  set,
  uid,
  selfUids,
  districtId,
  expanded,
  onToggle,
  onOpenForm,
}: AssignmentSetRowProps) {
  const selfKey = selfUids.join('|');
  const postIds = useMemo(
    () => resolveAssignedPostIds(set, uid, selfUids),
    // selfUids は array 参照が毎回変わるため join key で安定化
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [set, uid, selfKey],
  );
  const [posts, setPosts] = useState<PostFull[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);

  // expanded のとき、postIds から各 post の summary を fetch (one-shot)
  useEffect(() => {
    if (!expanded || postIds.length === 0) {
      setPosts([]);
      return;
    }
    let cancelled = false;
    setLoadingPosts(true);
    void Promise.all(
      postIds.map(async (pid) => {
        try {
          const ref = doc(db, 'districts', districtId, 'posts', pid);
          const snap = await getDoc(ref);
          if (!snap.exists()) return null;
          return mapPostSummary({ id: snap.id, data: () => snap.data() });
        } catch {
          return null;
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      setPosts(results.filter((p): p is PostFull => p !== null));
      setLoadingPosts(false);
    });
    return () => {
      cancelled = true;
    };
  }, [expanded, postIds, districtId]);

  const endAtStr = useMemo(() => {
    if (!set.endAt) return null;
    try {
      const d = new Date(set.endAt);
      const now = Date.now();
      const diffDays = Math.ceil((d.getTime() - now) / (1000 * 60 * 60 * 24));
      if (diffDays <= 0) return `(期限切れ)`;
      return `あと ${diffDays} 日`;
    } catch {
      return null;
    }
  }, [set.endAt]);

  return (
    <li className="border border-slate-200 rounded-lg bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-3 bg-transparent border-none cursor-pointer hover:bg-slate-50 rounded-lg"
      >
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">{expanded ? '▼' : '▶'}</span>
          <span className="font-medium flex-1 text-slate-900 truncate">
            {set.displayName || '(無題)'}
          </span>
          {endAtStr && <span className="text-[11px] text-slate-500 shrink-0">{endAtStr}</span>}
        </div>
        {set.description && (
          <p className="text-xs text-slate-600 mt-1 line-clamp-2">{set.description}</p>
        )}
        <p className="text-[11px] text-slate-400 mt-1">
          配分: {distributionLabel(set.distribution)} · 割当 {postIds.length} 件
        </p>
      </button>
      {expanded && (
        <div className="px-3 pb-3">
          {set.distribution === 'free' && (
            <p className="text-xs text-slate-500 py-2">
              この配分セットは「自由検証」モードです。上部の「📋 自由検証」セクションから任意の AI
              解析済投稿を選んで回答してください。
            </p>
          )}
          {set.distribution !== 'free' && postIds.length === 0 && (
            <p className="text-xs text-slate-500 py-2">
              この配分セットに割当てられた投稿はありません。
            </p>
          )}
          {loadingPosts && <p className="text-xs text-slate-500 py-2">投稿情報を取得中…</p>}
          {!loadingPosts && posts.length > 0 && (
            <ul className="grid gap-1.5 mt-1">
              {posts.map((p) => (
                <AssignmentPostRow
                  key={p.id}
                  post={p}
                  uid={uid}
                  selfUids={selfUids}
                  districtId={districtId}
                  onOpenForm={() => onOpenForm(p.id)}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

function AssignmentPostRow({
  post,
  uid,
  selfUids,
  districtId,
  onOpenForm,
}: {
  post: PostFull;
  uid: string;
  selfUids: string[];
  districtId: string;
  onOpenForm: () => void;
}) {
  const my = useMyVerification(districtId, post.id, uid, selfUids);
  const responded = my.status === 'ready' && my.verification !== null;
  return (
    <li>
      <button
        type="button"
        onClick={onOpenForm}
        className="w-full text-left p-2.5 border border-slate-200 rounded bg-white hover:bg-slate-50 cursor-pointer flex items-center gap-2"
      >
        <span className="flex-1 text-sm text-slate-800 truncate">
          {post.title}
          <span className="ml-2 text-[11px] text-slate-400">by {post.authorNickname}</span>
        </span>
        {responded ? (
          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded shrink-0">
            ✓ 回答済
          </span>
        ) : (
          <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded shrink-0">
            回答する
          </span>
        )}
      </button>
    </li>
  );
}

/**
 * 配分セット内で「現在の投稿の次にある未完了 (completed !== true) の投稿 ID」を返す
 * (Phase G、old2 VerificationFormPage findNextUnansweredPostId L670-693 逐語)。
 * 全部完了済なら null。末尾まで無ければ先頭側もチェックする。
 */
async function findNextUnansweredPostId(
  districtId: string,
  uid: string,
  assignedPostIds: string[],
  currentPostId: string,
  /** R-70 P3b: 現 UID + 端末引き継ぎ元 UID 群。いずれかの UID で完了済なら回答済とみなす。 */
  selfUids?: string[],
): Promise<string | null> {
  const uids = selfUids && selfUids.length > 0 ? selfUids : [uid];
  const currentIdx = assignedPostIds.indexOf(currentPostId);
  // 引き継ぎ後: いずれかの自分 UID で completed===true なら回答済 (= 未回答ではない)。
  const isUnanswered = async (id: string): Promise<boolean> => {
    for (const u of uids) {
      try {
        const snap = await getDoc(
          doc(db, 'districts', districtId, 'posts', id, 'verifications', u),
        );
        if (snap.exists() && snap.data()?.completed === true) return false;
      } catch {
        // read 不可 (claim 上限超 等) はその UID 分を未検出に縮退
      }
    }
    return true;
  };
  const after = currentIdx >= 0 ? assignedPostIds.slice(currentIdx + 1) : assignedPostIds;
  for (const id of after) {
    if (await isUnanswered(id)) return id;
  }
  const before = currentIdx > 0 ? assignedPostIds.slice(0, currentIdx) : [];
  for (const id of before) {
    if (await isUnanswered(id)) return id;
  }
  return null;
}

/** Form Modal 内部: template + post 取得後に VerificationForm を表示。 */
function VerificationFormModal({
  open,
  onClose,
  districtId,
  uid,
  selfUids,
  postId,
  assignmentSetId,
  templateId,
  assignedPostIds,
}: {
  open: boolean;
  onClose: () => void;
  districtId: string;
  uid: string;
  /** R-70 P3b: 現 UID + 端末引き継ぎ元 UID 群 (既存回答の pre-fill / 連続回答の完了判定の横断用)。 */
  selfUids: string[];
  postId: string;
  assignmentSetId: string | null;
  templateId: string;
  /** Phase G: 配分セットで自分に割り当てられた投稿 ID 群 (連続回答の遷移先解決用)。 */
  assignedPostIds: string[];
}) {
  const templateState = useVerificationTemplate(districtId, templateId);
  // Phase G (連続回答): modal を閉じずに次の投稿へ差し替えるための現在 postId state。
  const [currentPostId, setCurrentPostId] = useState(postId);
  useEffect(() => {
    setCurrentPostId(postId);
  }, [postId]);
  const myVerification = useMyVerification(districtId, currentPostId, uid, selfUids);
  const [post, setPost] = useState<PostFull | null>(null);
  const [postLoading, setPostLoading] = useState(true);
  const [postError, setPostError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPostLoading(true);
    setPostError(null);
    void getDoc(doc(db, 'districts', districtId, 'posts', currentPostId))
      .then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          setPost(null);
          setPostLoading(false);
          return;
        }
        setPost(mapPostSummary({ id: snap.id, data: () => snap.data() }));
        setPostLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setPostError(e);
        setPostLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [districtId, currentPostId]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="🤖 AI 解析の検証"
      size="lg"
      closeOnOverlayClick={false}
    >
      {postLoading && <p className="text-slate-500 text-center py-6">投稿情報を取得中…</p>}
      {postError && (
        <p className="text-danger-700 text-center py-6">取得エラー: {postError.message}</p>
      )}
      {!postLoading && !post && (
        <p className="text-slate-500 text-center py-6">投稿が見つかりません。</p>
      )}
      {templateState.status === 'loading' && (
        <p className="text-slate-500 text-center py-6">テンプレートを取得中…</p>
      )}
      {templateState.status === 'error' && (
        <p className="text-danger-700 text-center py-6">
          テンプレート取得エラー: {templateState.error.message}
        </p>
      )}
      {templateState.status === 'not-found' && (
        <p className="text-slate-500 text-center py-6">テンプレートが見つかりません。</p>
      )}
      {post && templateState.status === 'ready' && (
        <VerificationForm
          key={currentPostId}
          districtId={districtId}
          uid={uid}
          selfUids={selfUids}
          post={post as VerificationFormPost}
          template={templateState.template}
          assignmentSetId={assignmentSetId}
          existingVerification={
            myVerification.status === 'ready' ? myVerification.verification : null
          }
          onSubmitted={onClose}
          onCancel={onClose}
          // Phase G (連続回答): 配分セット経由かつ割当が 2 件以上あるときのみ自動遷移を有効化。
          findNextUnansweredPostId={
            assignmentSetId && assignedPostIds.length > 1
              ? (currentId) =>
                  findNextUnansweredPostId(districtId, uid, assignedPostIds, currentId, selfUids)
              : undefined
          }
          onAdvanceToPost={
            assignmentSetId && assignedPostIds.length > 1
              ? (nextId) => setCurrentPostId(nextId)
              : undefined
          }
        />
      )}
    </Modal>
  );
}

/** 自由検証セクション (Sprint Y) — AI 解析済投稿 listing + 「検証する」 button。 */
function FreeVerificationSection({
  districtId,
  uid,
  selfUids,
  activeTemplateId,
  onOpenForm,
}: {
  districtId: string;
  uid: string;
  /** R-70 P3b: 現 UID + 端末引き継ぎ元 UID 群 (回答済み判定の横断用)。 */
  selfUids: string[];
  activeTemplateId: string;
  onOpenForm: (postId: string, templateId: string) => void;
}) {
  const candidates = useFreeVerificationCandidates(districtId, uid, selfUids);

  return (
    <section className="mb-4 border border-primary-200 bg-primary-50/40 rounded-lg p-3">
      <h3 className="text-sm font-bold text-primary-900 flex items-center gap-1.5">
        <span>📋</span>
        <span>自由検証</span>
      </h3>
      <p className="text-xs text-slate-600 mt-1 mb-2">
        AI 解析済の任意投稿に対して検証する (どの投稿でも自由に選べます)。
      </p>
      {candidates.status === 'loading' && (
        <p className="text-xs text-slate-500 py-2">投稿一覧を取得中…</p>
      )}
      {candidates.status === 'error' && (
        <p className="text-xs text-danger-700 py-2">取得エラー: {candidates.error.message}</p>
      )}
      {candidates.status === 'ready' && candidates.posts.length === 0 && (
        <p className="text-xs text-slate-500 py-2">AI 解析済の投稿はまだありません。</p>
      )}
      {candidates.status === 'ready' && candidates.posts.length > 0 && (
        <ul className="grid gap-1.5 mt-1">
          {candidates.posts.map((p) => (
            <FreeVerificationPostRow
              key={p.id}
              post={p}
              uid={uid}
              selfUids={selfUids}
              districtId={districtId}
              onOpenForm={() => onOpenForm(p.id, activeTemplateId)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function FreeVerificationPostRow({
  post,
  uid,
  selfUids,
  districtId,
  onOpenForm,
}: {
  post: PostMinimal;
  uid: string;
  selfUids: string[];
  districtId: string;
  onOpenForm: () => void;
}) {
  const my = useMyVerification(districtId, post.id, uid, selfUids);
  const responded = my.status === 'ready' && my.verification !== null;
  // 【廃止予定 fallback】ecw/bousai/walker は legacy 地区の投稿のみ。新規地区は generic を読む。
  const genericDescription =
    typeof post.aiAnalysisGeneric?.description === 'string'
      ? post.aiAnalysisGeneric.description
      : null;
  const description =
    post.aiAnalysisEcw?.description ??
    post.aiAnalysisBousai?.description ??
    post.aiAnalysisWalker?.description ??
    genericDescription;
  return (
    <li>
      <button
        type="button"
        onClick={onOpenForm}
        className="w-full text-left p-2.5 border border-slate-200 rounded bg-white hover:bg-slate-50 cursor-pointer flex items-center gap-2"
      >
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-800 truncate">
            {post.title || '(無題)'}
            <span className="ml-2 text-[11px] text-slate-400">
              by {post.authorNickname || '匿名'}
            </span>
          </p>
          {description && (
            <p className="text-[11px] text-slate-500 line-clamp-1 mt-0.5">{description}</p>
          )}
        </div>
        {responded ? (
          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded shrink-0">
            ✓ 回答済
          </span>
        ) : (
          <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded shrink-0">
            検証する
          </span>
        )}
      </button>
    </li>
  );
}

export function VerificationListPage({
  districtId,
  districtName,
  uid,
  currentSessionId,
  open,
  onClose,
}: VerificationListPageProps) {
  const sets = useAssignmentSets(districtId, currentSessionId);
  const configState = useDistrictVerificationConfig(districtId);
  // R-70 P3b: 端末引き継ぎ後も「自分の回答」を横断判定するための現UID+引き継ぎ元UID群。
  const identity = useMyIdentity(uid, districtId);
  const selfUids = identity.allUids;
  // 研究利用からの除外 (同意撤回) 用: 自分の回答件数 + 撤回 action (Phase G、old2 逐語)。
  const countDistrictName = districtName ?? 'この地区';
  const myCount = useMyVerificationCount(districtId, uid, countDistrictName);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState<{
    postId: string;
    templateId: string;
    assignmentSetId: string | null;
    /** Phase G: 配分セットで自分に割り当てられた投稿 ID 群 (連続回答の遷移先解決用)。 */
    assignedPostIds: string[];
  } | null>(null);

  // 自由検証 (Sprint Y): config.allowFreeVerification && activeTemplateId 有りで表示
  const freeEnabled =
    configState.status === 'ready' &&
    configState.config.allowFreeVerification &&
    configState.config.activeTemplateId !== null;
  const activeTemplateId =
    configState.status === 'ready' ? configState.config.activeTemplateId : null;

  // List page では distribution='free' な set は非表示 (上部 free セクション経路で扱うため)
  const visibleSets = useMemo(() => {
    if (sets.status !== 'ready') return [];
    return sets.items.filter((s) => s.distribution !== 'free');
  }, [sets]);

  // 研究利用からの除外 (撤回) — confirm は old2 逐語、本人の全回答を物理削除する。
  function handleWithdraw() {
    if (myCount.state.status !== 'ready' || myCount.state.count === 0) return;
    const ok = confirm(
      `${countDistrictName} に送信したあなたの検証回答 ${myCount.state.count} 件をすべて削除します。\n\nこの操作は取り消せません（旧回答は復元できません）。よろしいですか？`,
    );
    if (!ok) return;
    void myCount.withdraw();
  }

  if (!open) return null;

  function handleOpenForm(set: AiVerificationAssignmentSet, postId: string) {
    setOpenForm({
      postId,
      templateId: set.templateId,
      assignmentSetId: set.id,
      // Phase G: 連続回答の遷移先解決のため、このセットでの自分の割当順序を渡す。
      // R-70 P3b: 引き継ぎ元 UID 宛ての割当も含める。
      assignedPostIds: resolveAssignedPostIds(set, uid, selfUids),
    });
  }

  function handleOpenFreeForm(postId: string, templateId: string) {
    setOpenForm({
      postId,
      templateId,
      assignmentSetId: null,
      // 自由検証は連続回答対象外 (1 投稿ごとに閉じる)。
      assignedPostIds: [],
    });
  }

  return (
    <>
      <Modal open={open} onClose={onClose} title="🤖 AI 解析の検証" size="md">
        {/* 研究利用説明 banner (Phase G、old2 VerificationListPage L137-151 逐語)。
            「いただいた回答は、まちづくりの手法の研究や地域のまちづくりのために利用させていただきます。」 */}
        <div className="bg-primary-50 border border-primary-200 rounded p-3 mb-4">
          <div className="flex gap-2 items-start">
            <div className="text-2xl">🤖</div>
            <div>
              <h2 className="font-semibold mb-1 text-slate-900">
                AI 解析結果の確認にご協力ください
              </h2>
              <p className="text-sm text-slate-700">
                写真付き投稿に対して AI が解析や分類を行っています。
                その解析が、人の目による解釈や現地の実情に合っているかを、皆さんで確認してください。
                <strong>
                  いただいた回答は、まちづくりの手法の研究や地域のまちづくりのために利用させていただきます。
                </strong>
              </p>
            </div>
          </div>
        </div>
        {freeEnabled && activeTemplateId !== null && (
          <FreeVerificationSection
            districtId={districtId}
            uid={uid}
            selfUids={selfUids}
            activeTemplateId={activeTemplateId}
            onOpenForm={handleOpenFreeForm}
          />
        )}
        {sets.status === 'loading' && <p className="text-slate-500 text-center py-6">読込中…</p>}
        {sets.status === 'error' && (
          <p className="text-danger-700 text-center py-6">取得エラー: {sets.error.message}</p>
        )}
        {sets.status === 'ready' && visibleSets.length === 0 && !freeEnabled && (
          <p className="text-slate-500 text-center py-6">現在検証する投稿はありません。</p>
        )}
        {sets.status === 'ready' && visibleSets.length > 0 && (
          <ul className="grid gap-2">
            {visibleSets.map((set) => (
              <AssignmentSetRow
                key={set.id}
                set={set}
                uid={uid}
                selfUids={selfUids}
                districtId={districtId}
                expanded={expandedId === set.id}
                onToggle={() => setExpandedId((cur) => (cur === set.id ? null : set.id))}
                onOpenForm={(pid) => handleOpenForm(set, pid)}
              />
            ))}
          </ul>
        )}

        {/* 研究利用からの除外 (同意撤回) section (Phase G、old2 VerificationListPage L254-275 逐語)。
            自分の検証回答が 1 件以上あるときのみ表示。「研究利用から除外する（削除）」で物理削除。 */}
        {myCount.state.status === 'ready' && myCount.state.count > 0 && (
          <section className="mt-8 border border-slate-300 rounded p-4 bg-slate-50">
            <h2 className="text-sm font-semibold mb-2 text-slate-900">🛡️ 研究利用について</h2>
            <p className="text-xs text-slate-700 leading-relaxed">
              {countDistrictName} に送信したあなたの検証回答{' '}
              <strong>{myCount.state.count} 件</strong>{' '}
              は、まちづくりの研究や地域のまちづくりのために利用させていただきます。
              利用を希望されない場合は、以下から削除できます（取り消しはできません）。
            </p>
            <button
              type="button"
              onClick={handleWithdraw}
              disabled={myCount.withdrawing}
              className="mt-3 px-3 py-2 text-sm border border-danger-700 text-danger-700 rounded hover:bg-danger-50 disabled:opacity-50"
            >
              {myCount.withdrawing ? '削除中…' : '私の検証回答を研究利用から除外する（削除）'}
            </button>
            {myCount.message && <p className="mt-2 text-xs text-slate-700">{myCount.message}</p>}
          </section>
        )}
      </Modal>
      {openForm && (
        <VerificationFormModal
          open={true}
          onClose={() => setOpenForm(null)}
          districtId={districtId}
          uid={uid}
          selfUids={selfUids}
          postId={openForm.postId}
          assignmentSetId={openForm.assignmentSetId}
          templateId={openForm.templateId}
          assignedPostIds={openForm.assignedPostIds}
        />
      )}
    </>
  );
}
